import React, { useState, useEffect } from 'react';
import {
    Building2, Settings, ShieldAlert, Search, UserPlus,
    X, CheckCircle, Mail, Lock, Trash2, Globe, MoreHorizontal,
    Power, PowerOff, ShieldCheck, AlertTriangle, Loader2
} from 'lucide-react';
import api from '../api/axiosInstance';

const CompanyManager = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modals & Forms State
    const [isRegModalOpen, setIsRegModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [registerLoading, setRegisterLoading] = useState(false);

    const [formData, setFormData] = useState({
        company_name: '',
        owner_email: '',
        owner_password: '',
    });

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const response = await api.get('get_companies.php');
            if (response.data.status === "success") {
                setCompanies(response.data.companies);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- ACTION: Register New Tenant ---
    const handleRegisterTenant = async (e) => {
        e.preventDefault();
        setRegisterLoading(true);
        try {
            const response = await api.post('register_company.php', formData);
            if (response.data.status === "success") {
                setIsRegModalOpen(false);
                setFormData({ company_name: '', owner_email: '', owner_password: '' });
                fetchCompanies(); // Refresh list
                alert("New Tenant Registered Successfully!");
            } else {
                alert(response.data.message || "Registration failed");
            }
        } catch (err) {
            alert("Network error. Please check backend.");
        } finally {
            setRegisterLoading(false);
        }
    };

    // --- ACTION: Toggle Status (Enable/Disable) ---
    const handleToggleStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            const response = await api.post('update_company_status.php', { id, status: newStatus });
            if (response.data.status === "success") {
                setCompanies(companies.map(c => c.id === id ? { ...c, status: newStatus } : c));
            }
        } catch (err) {
            alert("Failed to update status");
        }
    };

    // --- ACTION: Delete Company ---
    const handleDeleteCompany = async (id, name) => {
        if (window.confirm(`Are you sure you want to PERMANENTLY delete ${name}? This will remove all their projects, users, and data.`)) {
            try {
                const response = await api.post('delete_company.php', { id });
                if (response.data.status === "success") {
                    setCompanies(companies.filter(c => c.id !== id));
                    setSelectedCompany(null);
                }
            } catch (err) {
                alert("Error deleting company");
            }
        }
    };

    const filteredCompanies = companies.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8 relative p-4 md:p-8 animate-in fade-in duration-500">
            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">Master Console</h1>
                    <p className="text-slate-500 font-medium italic">SaaS Master Control / {companies.length} Tenants</p>
                </div>
                <button
                    onClick={() => setIsRegModalOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-black shadow-xl shadow-indigo-100 transition-all flex items-center gap-3 hover:scale-105 active:scale-95"
                >
                    <UserPlus size={20} /> Add New Tenant
                </button>
            </div>

            {/* --- SEARCH BAR --- */}
            <div className="relative group">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={22} />
                <input
                    type="text" placeholder="Search by company name..."
                    className="w-full bg-white border border-slate-100 rounded-[30px] py-6 pl-16 pr-6 outline-none focus:ring-4 focus:ring-indigo-50 transition-all font-bold text-slate-700 shadow-sm text-lg"
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* --- TENANT TABLE --- */}
            <div className="bg-white rounded-[48px] shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead>
                        <tr className="bg-slate-50/80 border-b border-slate-100">
                            <th className="p-8 text-[11px] uppercase tracking-widest text-slate-400 font-black">Identity</th>
                            <th className="p-8 text-[11px] uppercase tracking-widest text-slate-400 font-black">Health Status</th>
                            <th className="p-8 text-right text-[11px] uppercase tracking-widest text-slate-400 font-black">Management</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {loading ? (
                            <tr><td colSpan="3" className="p-20 text-center font-black italic text-slate-300">Syncing Master Data...</td></tr>
                        ) : filteredCompanies.map((company) => (
                            <tr key={company.id} className="group hover:bg-slate-50/50 transition-all">
                                <td className="p-8">
                                    <div className="flex items-center gap-6">
                                        <div className={`w-14 h-14 rounded-3xl flex items-center justify-center font-black border-2 transition-all ${company.status === 'active' ? 'bg-indigo-50 border-indigo-100 text-indigo-600' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                                            {company.name[0]}
                                        </div>
                                        <div>
                                            <p className="font-black text-slate-900 text-lg uppercase italic tracking-tight">{company.name}</p>
                                            <p className="text-xs text-slate-400 font-black uppercase tracking-tighter flex items-center gap-1"><Mail size={12} /> {company.owner_email}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-8">
                                    <button
                                        onClick={() => handleToggleStatus(company.id, company.status)}
                                        className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-sm ${company.status === 'active'
                                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                            : 'bg-rose-50 text-rose-600 border border-rose-100'
                                            }`}
                                    >
                                        {company.status === 'active' ? <Power size={14} /> : <PowerOff size={14} />}
                                        {company.status}
                                    </button>
                                </td>
                                <td className="p-8 text-right">
                                    <div className="flex justify-end gap-3">
                                        <button
                                            onClick={() => setSelectedCompany(company)}
                                            className="p-4 bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-all shadow-sm hover:shadow-lg"
                                        >
                                            <Settings size={20} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteCompany(company.id, company.name)}
                                            className="p-4 bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-rose-600 hover:border-rose-200 transition-all shadow-sm hover:shadow-lg"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- MODAL: REGISTRATION --- */}
            {isRegModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-in fade-in duration-300" onClick={() => setIsRegModalOpen(false)}></div>
                    <form onSubmit={handleRegisterTenant} className="bg-white w-full max-w-xl rounded-[48px] shadow-2xl relative z-10 p-12 border border-white animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-10">
                            <div>
                                <h2 className="text-3xl font-black text-slate-900 italic tracking-tighter">Onboard New Tenant</h2>
                                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Create Company & Admin Profile</p>
                            </div>
                            <button type="button" onClick={() => setIsRegModalOpen(false)} className="p-3 text-slate-400 hover:bg-slate-50 rounded-full transition-colors"><X size={24} /></button>
                        </div>

                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Company Name</label>
                                <div className="relative">
                                    <Building2 className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                    <input
                                        required className="w-full bg-slate-50 border-none rounded-3xl py-5 pl-14 pr-4 font-bold outline-none ring-2 ring-transparent focus:ring-indigo-500 transition-all"
                                        value={formData.company_name}
                                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                        placeholder="Ex: ABC Interior"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Owner Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                    <input
                                        type="email" required
                                        className="w-full bg-slate-50 border-none rounded-3xl py-5 pl-14 pr-4 font-bold outline-none ring-2 ring-transparent focus:ring-indigo-500 transition-all"
                                        value={formData.owner_email}
                                        onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                                        placeholder="owner@domain.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Initial Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                    <input
                                        type="password" required
                                        className="w-full bg-slate-50 border-none rounded-3xl py-5 pl-14 pr-4 font-bold outline-none ring-2 ring-transparent focus:ring-indigo-500 transition-all"
                                        value={formData.owner_password}
                                        onChange={(e) => setFormData({ ...formData, owner_password: e.target.value })}
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <button
                                disabled={registerLoading}
                                className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-lg uppercase tracking-widest shadow-2xl hover:bg-indigo-600 transition-all flex items-center justify-center gap-3 disabled:opacity-50 mt-4"
                            >
                                {registerLoading ? <Loader2 className="animate-spin" /> : <CheckCircle />}
                                Confirm & Initialize
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* --- MODAL: QUICK SETTINGS --- */}
            {selectedCompany && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setSelectedCompany(null)}></div>
                    <div className="bg-white w-full max-w-md rounded-[48px] shadow-2xl relative z-10 p-12 border border-white animate-in slide-in-from-bottom-10 duration-500">
                        <div className="flex justify-between items-center mb-10">
                            <h2 className="text-2xl font-black text-slate-900 italic tracking-tighter">Tenant Options</h2>
                            <button onClick={() => setSelectedCompany(null)} className="p-2 text-slate-400 hover:bg-slate-50 rounded-full transition-colors"><X size={24} /></button>
                        </div>

                        <div className="space-y-6">
                            <div className="p-8 bg-slate-50 rounded-[32px] border border-slate-100">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Company Context</p>
                                <p className="text-xl font-black text-slate-900 italic uppercase">{selectedCompany.name}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <button className="flex flex-col items-center justify-center p-8 bg-indigo-50 rounded-[32px] border border-indigo-100 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all group">
                                    <ShieldCheck size={28} className="mb-2" />
                                    <span className="text-[10px] font-black uppercase">ACL Rules</span>
                                </button>
                                <button className="flex flex-col items-center justify-center p-8 bg-amber-50 rounded-[32px] border border-amber-100 text-amber-600 hover:bg-amber-600 hover:text-white transition-all group">
                                    <AlertTriangle size={28} className="mb-2" />
                                    <span className="text-[10px] font-black uppercase">Reset Pwd</span>
                                </button>
                            </div>

                            <button
                                onClick={() => handleDeleteCompany(selectedCompany.id, selectedCompany.name)}
                                className="w-full py-5 text-rose-500 font-black text-[11px] uppercase tracking-widest bg-rose-50 rounded-[28px] border border-rose-100 hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                            >
                                Terminate Entire Account
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyManager;